import {useEffect, useState} from "react";
import {
  Box, Typography, Tabs, Tab, IconButton, Button, CircularProgress, Avatar, Chip, Tooltip,
} from "@mui/material";
import {
  ArrowForward as BackIcon,
  VisibilityOutlined as ImpersonateIcon,
} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {getCompanyUsersByRole} from "../../services/system/CompanyService";
import {useAuth} from "../../contexts/AuthContext";
import {useMessageContext} from "../../contexts/MessageContext";
import {t} from "./coach/coachTokens";

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0)).join("").toUpperCase() || "?";

// A row for one company user. A disabled user cannot be impersonated (the server refuses to build a
// session for a disabled account), so we mark them "מושבת" and gray out the impersonate button.
const UserRow = ({user, onImpersonate, busy}) => {
  const disabled = user.enabled === false;
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: "10px 14px", borderRadius: "12px", background: t.white,
        border: `1px solid ${t.border}`,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Avatar sx={{width: 34, height: 34, fontSize: 13, bgcolor: disabled ? t.t3 : t.green, color: "#fff"}}>
        {initials(user.name)}
      </Avatar>
      <Box sx={{flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1}}>
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: t.text, lineHeight: 1.2}} noWrap>
            {user.name || "—"}
          </Typography>
          <Typography sx={{fontSize: 12, color: t.t2}} noWrap>{user.email}</Typography>
        </Box>
        {disabled && (
          <Chip
            label="מושבת"
            size="small"
            sx={{
              height: 20, fontSize: 11, fontWeight: 700,
              color: t.red, background: t.redLight || "#FEECEC",
            }}
          />
        )}
      </Box>
      <Tooltip title={disabled ? "לא ניתן להתחזות למשתמש מושבת" : ""} disableHoverListener={!disabled}>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ImpersonateIcon sx={{fontSize: 16}} />}
            disabled={busy || disabled}
            onClick={() => onImpersonate(user)}
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: 12, borderRadius: "8px",
              color: "#7C3AED", borderColor: "#7C3AED",
              "&:hover": {borderColor: "#6D28D9", background: "#F3E8FF"},
            }}
          >
            התחזה
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

// The dashboard drill-down for one company: one tab per impersonatable persona, each listing users the
// super-admin can act as. Selecting "התחזה" swaps the session to that user (see AuthContext).
//
// Naming note: the ADMIN tab is labelled "מנהלי חברה" rather than the older "מנהלים", because the
// MANAGER persona — a company user with delegated, partial admin authority — is the one users now call
// "מנהל" (it is what the sidebar's Managers screen creates). Leaving both tabs as "מנהלים" would make
// the two indistinguishable at exactly the moment a super-admin is choosing whose session to enter.
//
// Impersonating a MANAGER yields that manager's own capabilities and trainer scope, because all
// scoping runs off the impersonated principal rather than any special-casing here.
const TABS = [
  {value: "ADMIN", label: "מנהלי חברה", empty: "אין מנהלי חברה בחברה זו"},
  {value: "MANAGER", label: "מנהלים", empty: "אין מנהלים בחברה זו"},
  {value: "TRAINER", label: "מאמנים", empty: "אין מאמנים בחברה זו"},
];
const CompanyImpersonatePanel = ({company, onBack}) => {
  const {impersonate} = useAuth();
  const {notificationHandler} = useMessageContext();

  const [tab, setTab] = useState("ADMIN"); // one of TABS[].value
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);

  useEffect(() => {
    setLoading(true);
    getCompanyUsersByRole(company.id, tab)
      .then((res) => setUsers(res.data || []))
      .catch(() => notificationHandler.error("טעינת המשתמשים נכשלה"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id, tab]);

  const handleImpersonate = async (user) => {
    setBusyUserId(user.id);
    try {
      await impersonate(user.id, user.name);
      // AuthContext swapped the token + reloaded the user; the app re-renders as that user.
    } catch (e) {
      notificationHandler.error("ההתחזות נכשלה");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <RTL>
      <Box dir="rtl" sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
        {/* Header with back */}
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
          <IconButton size="small" onClick={onBack} sx={{color: t.t2}}>
            <BackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography sx={{fontSize: 20, fontWeight: 800, color: t.text, lineHeight: 1.2}}>
              {company.name}
            </Typography>
            <Typography sx={{fontSize: 13, color: t.t2}}>
              בחר משתמש כדי לפעול בשמו (התחזות)
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{minHeight: 40, "& .MuiTab-root": {minHeight: 40, fontWeight: 700, fontSize: 14}}}
        >
          {TABS.map((t2) => (
            <Tab key={t2.value} value={t2.value} label={t2.label} />
          ))}
        </Tabs>

        {loading ? (
          <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
            <CircularProgress size={24} sx={{color: t.green}} />
          </Box>
        ) : users.length === 0 ? (
          <Typography sx={{fontSize: 13, color: t.t2, py: 3, textAlign: "center"}}>
            {TABS.find((t2) => t2.value === tab)?.empty}
          </Typography>
        ) : (
          <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
            {users.map((u) => (
              <UserRow key={u.id} user={u} onImpersonate={handleImpersonate} busy={busyUserId === u.id} />
            ))}
          </Box>
        )}
      </Box>
    </RTL>
  );
};

export default CompanyImpersonatePanel;
